import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Search, UserCheck, Users, Archive, AlertTriangle } from "lucide-react";

export default function AssignmentsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [registry, setRegistry] = useState([]);
  const [q, setQ] = useState("");
  const [pmFor, setPmFor] = useState(null);
  const [resFor, setResFor] = useState(null);
  const allowed = ["supervisor", "admin"].includes(user?.role);

  const load = () => api.get("/assignments").then(({ data }) => setData(data)).catch(() => setData({ projects: [], pms: [] }));
  useEffect(() => {
    if (!allowed) return;
    load();
    api.get("/resource-registry").then(({ data }) => setRegistry(data)).catch(() => {});
  }, [allowed]);

  const shown = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.projects;
    return data.projects.filter((p) => `${p.name} ${p.owner?.name || ""} ${(p.resources || []).map((r) => r.name).join(" ")}`.toLowerCase().includes(needle));
  }, [data, q]);

  if (!allowed) return (
    <Layout>
      <div className="max-w-lg mx-auto text-center py-24" data-testid="assignments-restricted">
        <UserCheck className="h-10 w-10 text-violet-600 mx-auto mb-4" />
        <h1 className="font-display text-2xl font-extrabold tracking-tight">Project Assignments</h1>
        <p className="text-muted-foreground mt-2">Assigning projects to PMs and resources is available to supervisors and admins.</p>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="space-y-8" data-testid="assignments-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-3 py-1">
              <UserCheck className="h-3.5 w-3.5" /> Portfolio layer
            </span>
            <h1 className="font-display text-4xl font-extrabold tracking-tight mt-3">Project Assignments</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">Assign projects to project managers (ownership transfers to their workspace) and staff them with resources from the registry. Everyone gets notified by email.</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects, PMs, resources…" className="pl-9" data-testid="assignments-search" />
          </div>
        </div>

        {data === null && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}

        {data !== null && (
          <div className="rounded-xl border border-border bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5 font-bold">Project</th>
                  <th className="px-3 py-2.5 font-bold">Project Manager</th>
                  <th className="px-3 py-2.5 font-bold">Resources</th>
                  <th className="px-3 py-2.5 font-bold text-right">Tasks</th>
                  <th className="px-3 py-2.5 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">{q ? `No projects match "${q}".` : "No projects yet."}</td></tr>
                )}
                {shown.map((p) => (
                  <tr key={p.id} className="border-b border-border/60 last:border-0 align-top" data-testid={`assignment-row-${p.id}`}>
                    <td className="px-4 py-2.5 max-w-[240px]">
                      <p className="font-semibold truncate flex items-center gap-1.5">
                        {p.archived && <Archive className="h-3 w-3 text-muted-foreground shrink-0" />} {p.name}
                      </p>
                      {p.assigned_by_name && <p className="text-[11px] text-muted-foreground mt-0.5">assigned by {p.assigned_by_name}{p.assigned_at ? ` · ${new Date(p.assigned_at).toLocaleDateString()}` : ""}</p>}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <p className="font-medium">{p.owner?.name || "—"}</p>
                      <p className="text-[11px] text-muted-foreground">{p.owner?.email}</p>
                    </td>
                    <td className="px-3 py-2.5 max-w-[260px]">
                      {(p.resources || []).length === 0
                        ? <span className="text-xs text-muted-foreground">None</span>
                        : (
                          <div className="flex flex-wrap gap-1">
                            {p.resources.slice(0, 5).map((r) => (
                              <span key={r.name} className="text-[11px] font-semibold bg-secondary rounded-full px-2 py-0.5">{r.name}</span>
                            ))}
                            {p.resources.length > 5 && <span className="text-[11px] text-muted-foreground">+{p.resources.length - 5} more</span>}
                          </div>
                        )}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{p.task_count}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs gap-1" onClick={() => setPmFor(p)} data-testid={`assign-pm-${p.id}`}>
                          <UserCheck className="h-3.5 w-3.5" /> Assign PM
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs gap-1" onClick={() => setResFor(p)} data-testid={`assign-resources-${p.id}`}>
                          <Users className="h-3.5 w-3.5" /> Resources
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AssignPmDialog project={pmFor} pms={data?.pms || []} onOpenChange={(v) => !v && setPmFor(null)} onDone={() => { setPmFor(null); load(); }} />
        <AssignResourcesDialog project={resFor} registry={registry} onOpenChange={(v) => !v && setResFor(null)} onDone={() => { setResFor(null); load(); }} />
      </div>
    </Layout>
  );
}

function AssignPmDialog({ project, pms, onOpenChange, onDone }) {
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setUserId(""); }, [project?.id]);
  const candidates = pms.filter((u) => u.id !== project?.owner_id);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/assignments/${project.id}/pm`, { user_id: userId });
      toast.success(`"${project.name}" assigned to ${data.owner.name}${data.emailed ? " — they've been emailed" : ""}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not assign the project"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!project} onOpenChange={onOpenChange}>
      <DialogContent data-testid="assign-pm-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><UserCheck className="h-5 w-5 text-violet-600" /> Assign project manager</DialogTitle>
          <DialogDescription>{project?.name} — currently with {project?.owner?.name || "unknown"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>New project manager</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="mt-1.5" data-testid="assign-pm-select"><SelectValue placeholder="Pick a PM…" /></SelectTrigger>
              <SelectContent>
                {candidates.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name} ({u.email}){u.role !== "pm" ? ` · ${u.role}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Ownership fully transfers — the project moves to their workspace and leaves the current PM's project list.
          </p>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !userId} className="gap-2 bg-violet-600 hover:bg-violet-700" data-testid="assign-pm-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />} Assign & notify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignResourcesDialog({ project, registry, onOpenChange, onDone }) {
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPicked([]); }, [project?.id]);
  const inProject = new Set((project?.resources || []).map((r) => r.name.trim().toLowerCase()));
  const candidates = registry.filter((r) => !inProject.has(r.name.trim().toLowerCase()));

  const toggle = (name) => setPicked((s) => s.includes(name) ? s.filter((n) => n !== name) : [...s, name]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/assignments/${project.id}/resources`, { names: picked });
      toast.success(`Added ${data.added.length} resource${data.added.length === 1 ? "" : "s"} to "${project.name}"${data.emailed.length ? ` — ${data.emailed.length} emailed` : ""}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add resources"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!project} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="assign-resources-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Users className="h-5 w-5 text-violet-600" /> Add resources</DialogTitle>
          <DialogDescription>{project?.name} — pick people from the registry; they'll join the project's resource pool.</DialogDescription>
        </DialogHeader>
        {candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Everyone in your registry is already on this project (or the registry is empty).</p>
        ) : (
          <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
            {candidates.map((r) => (
              <label key={r.name} className="flex items-center gap-2.5 rounded-md border border-border px-3 py-2 cursor-pointer hover:bg-secondary/40" data-testid={`assign-resource-option-${r.name}`}>
                <Checkbox checked={picked.includes(r.name)} onCheckedChange={() => toggle(r.name)} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold truncate">{r.name}</span>
                  <span className="block text-[11px] text-muted-foreground truncate">{[r.role, r.email || "no email — won't be notified"].filter(Boolean).join(" · ")}</span>
                </span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button onClick={submit} disabled={busy || picked.length === 0} className="gap-2 bg-violet-600 hover:bg-violet-700" data-testid="assign-resources-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />} Add {picked.length || ""} & notify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
